import { useCallback } from 'react';
import { useDemand, ACTIONS } from '../../context/DemandContext';
import { useVersionControl } from './useVersionControl';
import { useDataTransformation } from './useDataTransformation';
import { useItemOperations, useGroupOperations, useEnsureGroup } from '../useOSApi';
import { message } from 'antd';
import { useSearchParams } from 'react-router-dom';

import { useFileManagementContext } from '../../context/FileManagementContext';
import { useNavigate } from 'react-router-dom';
import { GROUP_NAME, GROUP_DESCRIPTION, DEMAND_CONSTANTS } from '../../config/constants';

/**
 * 🎯 保存操作 Hook
 * 統一處理整個需求單保存流程
 */
export const useSaveOperation = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useDemand();
  const [searchParams] = useSearchParams();
  const { state: fileState, actions: fileActions } = useFileManagementContext();

  // 子 hooks
  const {
    prepareVersionData,
    checkVersionLimit,
    sourceItem,
    sourceBlob,
    sourceJsonFileId
  } = useVersionControl();

  const {
    transformToSaveFormat,
    prepareJsonFile,
    updateFileReferences,
    validateData
  } = useDataTransformation();

  // API hooks
  const { ensureGroupExists } = useEnsureGroup(GROUP_NAME.DEMAND, GROUP_DESCRIPTION.DEMAND);
  const { createGroupItem } = useGroupOperations();
  const {
    uploadBlobToItem,
    updateInformation,
    updateName,
    deleteItemBlob
  } = useItemOperations();

  const { saveOperation } = state;

  /**
   * 設置保存載入狀態
   * @param {boolean} loading - 是否載入中
   */
  const setSaveLoading = useCallback((loading) => {
    dispatch({
      type: ACTIONS.SET_SAVE_LOADING,
      payload: loading
    });
  }, [dispatch]);

  /**
   * 設置保存錯誤
   * @param {string|null} error - 錯誤信息
   */
  const setSaveError = useCallback((error) => {
    dispatch({
      type: ACTIONS.SET_SAVE_ERROR,
      payload: error
    });
  }, [dispatch]);

  /**
   * 設置保存進度
   * @param {number} progress - 進度百分比 (0-100)
   */
  const setSaveProgress = useCallback((progress) => {
    dispatch({
      type: ACTIONS.SET_SAVE_PROGRESS,
      payload: progress
    });
  }, [dispatch]);

  /**
   * 重置保存狀態
   */
  const resetSaveState = useCallback(() => {
    dispatch({
      type: ACTIONS.RESET_SAVE_STATE
    });
  }, [dispatch]);

  /**
   * 創建新的需求單項目
   * @returns {Promise<Object>} 創建的項目
   */
  const createItem = useCallback(async () => {
    const groupId = await ensureGroupExists();

    const requestData = {
      group_id: groupId,
      data: {
        name: state.basic?.name ?? DEMAND_CONSTANTS.DEFAULT_NAME,
        description: DEMAND_CONSTANTS.DEFAULT_DESCRIPTION,
        information: {},
      },
    };

    return await createGroupItem.mutateAsync(requestData);
  }, [ensureGroupExists, state.basic?.name, createGroupItem]);

  /**
   * 批次上傳檔案
   * @param {string} itemId - 項目 ID
   * @returns {Promise<Array>} 新上傳的檔案列表
   */
  const uploadBlobFiles = useCallback(async (itemId) => {
    // 1. 從 FileManagementContext 獲取需要上傳的檔案
    const pendingUploadFiles = fileState.filter(file => {
      // console.log('[uploadBlobFiles] file', file);
      let shouldUpload = false;

      switch (file.fileSource) {
        case 'local':
        case 'library':
          shouldUpload = true;
          break;
        case 'server':
        default:
          shouldUpload = false;
          break;
      }

      if (!shouldUpload) {
        console.log('跳過檔案:', file?.id, '原因:',
          file.fileSource === 'server' ? '伺服器檔案無需上傳' :
            `未知檔案來源: ${file.fileSource}`
        );
      }
      return shouldUpload;
    });

    const processedBlobIds = new Set();
    const newlyUploadedFiles = [];
    const totalFiles = pendingUploadFiles.length;

    setSaveProgress(10); // 開始上傳

    await Promise.all(
      pendingUploadFiles.map(async (file, index) => {
        try {
          // 直接上傳檔案，osApi 會處理 blob URL 的轉換
          const uploadResponse = await uploadBlobToItem.mutateAsync({
            item_id: itemId,
            file: file
          });

          // 找到新上傳的 blob
          const newBlob = uploadResponse.data.blobs
            .filter(blob => !processedBlobIds.has(blob.id))
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

          if (!newBlob) return;

          processedBlobIds.add(newBlob.id);
          
          // 🆕 記錄更詳細的檔案匹配資訊，包含備用匹配策略
          const fileMatchingInfo = {
            // 主要匹配策略：檔案 ID
            origin: {
              id: file.id,
              uid: file.uid,
              name: file.name,
              size: file.size,
              content_type: file.content_type
            },
            
            // 🆕 備用匹配策略：檔案位置
            fallbackMatching: {
              formField: file.formField || [],
              uploadContext: file.uploadContext || {},
              fileIndex: index, // 在上傳佇列中的順序
              uploadTimestamp: Date.now(),
              
              // 檔案特徵用於匹配
              fileSignature: {
                name: file.name,
                size: file.size,
                type: file.content_type,
                lastModified: file.originFileObj?.lastModified || null
              }
            },
            
            newFile: newBlob,
          };
          
          newlyUploadedFiles.push(fileMatchingInfo);

          // 更新進度
          const progress = 10 + Math.floor((index + 1) / totalFiles * 50);
          setSaveProgress(progress);

          console.log('[uploadBlobFiles] 檔案上傳成功:', {
            oldId: file.id,
            newBlobId: newBlob.id,
            formField: file.formField,
            uploadContext: file.uploadContext,
            response: uploadResponse
          });

        } catch (error) {
          console.error('檔案上傳失敗:', error);
          throw new Error(`檔案 ${file.name} 上傳失敗: ${error.message}`);
        }
      })
    );

    return newlyUploadedFiles;
  }, [fileState, uploadBlobToItem, setSaveProgress]);

  /**
   * 主要保存方法
   */
  const save = useCallback(async () => {
    try {
      // 重置錯誤狀態
      setSaveError(null);
      setSaveLoading(true);
      setSaveProgress(0);

      console.log(
        `%c[開始儲存需求單資料]`,
        'background: #67be5f; color: white; padding: 2px 4px; border-radius: 2px;',
      );
      console.log("[save] 版本數據", { sourceItem, sourceBlob, sourceJsonFileId });

      // 1. 表單驗證 (已在 DemandForm 中處理)
      setSaveProgress(3);

      // 2. 檢查版本限制
      const versionCheck = checkVersionLimit();
      console.log("[2. 檢查版本限制] versionCheck", versionCheck);
      if (!versionCheck.isValid) {
        throw new Error(versionCheck.message);
      }

      setSaveProgress(5);

      // 3. 準備數據
      const currentData = transformToSaveFormat();
      console.log("[3. 準備數據] currentData", currentData);

      // 4. 後端數據驗證
      const validation = validateData(currentData);
      console.log("[4. 後端數據驗證] validation", validation);
      if (!validation.isValid) {
        throw new Error(`數據驗證失敗: ${validation.errors.join(', ')}`);
      }

      // 5. 取得/創建 itemId
      const itemId = searchParams.get("id") || (await createItem())?.data?.id;
      console.log("[5. 取得/創建] itemId", itemId);
      setSaveProgress(15);

      // 6. 處理直播間 API (暫時停用)
      let newRoomCode = state.live?.roomCode || null;
      console.log("[6. 處理直播間 API] 直播功能暫時停用");
      setSaveProgress(20);

      // 7. 上傳檔案
      const newlyUploadedFiles = await uploadBlobFiles(itemId);
      console.log("[7. 上傳檔案] newlyUploadedFiles", newlyUploadedFiles);
      setSaveProgress(70);

      // 8. 準備版本數據
      const sourceData = prepareVersionData({
        ...currentData,
        live: {
          ...currentData.live,
          roomCode: newRoomCode
        }
      });
      console.log("[8. 準備版本數據] sourceData", sourceData);
      setSaveProgress(75);

      // 9. 更新檔案引用（僅在臨時數據中）
      const updatedSourceData = updateFileReferences(sourceData, newlyUploadedFiles);
      console.log("[9. 更新檔案引用] updatedSourceData", updatedSourceData);
      setSaveProgress(80);

      // 10. 準備並上傳 JSON 檔案
      const jsonBlob = prepareJsonFile(updatedSourceData);
      const uploadResponse = await uploadBlobToItem.mutateAsync({
        item_id: itemId,
        file: jsonBlob,
      });
      console.log("[10. 準備並上傳 JSON 檔案] uploadResponse", uploadResponse);
      setSaveProgress(85);

      // 11. 找到最新的 JSON blob
      if (!uploadResponse || !uploadResponse.data || !uploadResponse.data.blobs || !Array.isArray(uploadResponse.data.blobs)) {
        console.error("[11. 找到最新的 JSON blob] uploadResponse 格式錯誤:", uploadResponse);
        throw new Error("上傳 JSON 檔案後，伺服器回應格式不正確");
      }

      const newJsonBlob = uploadResponse.data.blobs
        .filter((uploadedBlob) => uploadedBlob.content_type === "application/json")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      // 12. 更新項目資訊
      if (newJsonBlob) {
        await updateInformation.mutateAsync({
          item_id: itemId,
          data: {
            jsonFile: newJsonBlob,
          },
        });
        console.log("[12. 更新項目資訊] newJsonBlob", newJsonBlob);

        // 13. 更新項目名稱
        if (state.basic?.name) {
          await updateName.mutateAsync({
            item_id: itemId,
            name: state.basic.name,
          });
        }
        console.log("[13. 更新項目名稱] state.basic.name", state.basic.name);

        // 14. 刪除舊的 JSON 檔案
        if (sourceJsonFileId) {
          await deleteItemBlob.mutateAsync({
            item_id: itemId,
            blob_id: sourceJsonFileId,
          });
        }
        console.log("[14. 刪除舊的 JSON 檔案] sourceJsonFileId", sourceJsonFileId);
      }

      setSaveProgress(100);
      message.success("需求單儲存成功!");

      // 重置編輯狀態
      dispatch({
        type: ACTIONS.RESET_STATE,
      });
      fileActions.resetFiles();

      // 返回需求單編號，讓調用方處理後續邏輯
      return {
        success: true,
        demandId: itemId,
        message: "需求單儲存成功!"
      };

    } catch (error) {
      const errorMessage = error.message || "儲存失敗";
      setSaveError(errorMessage);
      message.error(errorMessage);
      console.error("Save operation failed:", error);
      throw error;
    } finally {
      setSaveLoading(false);
      setTimeout(() => {
        setSaveProgress(0);
      }, 1000);
    }
  }, [
    setSaveError,
    setSaveLoading,
    setSaveProgress,
    checkVersionLimit,
    transformToSaveFormat,
    validateData,
    prepareVersionData,
    searchParams,
    createItem,
    uploadBlobFiles,
    updateFileReferences,
    prepareJsonFile,
    uploadBlobToItem,
    updateInformation,
    updateName,
    deleteItemBlob,
    state.basic?.name,
    state.live,
    dispatch
  ]);

  return {
    // 狀態
    isLoading: saveOperation.isLoading,
    error: saveOperation.error,
    progress: saveOperation.progress,

    // 主要操作
    save,

    // 狀態控制
    setSaveLoading,
    setSaveError,
    setSaveProgress,
    resetSaveState,

    // 內部方法（如果需要在外部使用）
    createItem,
    uploadBlobFiles
  };
}; 